import React, { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  InputAdornment,
  TextField,
  Tooltip,
  Checkbox,
  Alert,
  Snackbar
} from '@mui/material'
import AddIcon from '@mui/icons-material/AddRounded'
import EditIcon from '@mui/icons-material/EditOutlined'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import SearchIcon from '@mui/icons-material/SearchRounded'
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined'
import { useQuery, useMutation, useApolloClient } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { GET_ADVANCED_MODIFIER_GROUPS } from '../apollo/advancedMenuQueries'
import { DELETE_ADVANCED_MODIFIER_GROUP, DUPLICATE_ADVANCED_MODIFIER_GROUP } from '../apollo/advancedMenuMutations'
import Column3GroupBuilder from '../components/ModifierStudio/Column3_GroupBuilder'
import useBulkSelection, { bulkDelete } from '../components/Common/useBulkSelection'
import { mw, tableCardSx, headCellSx, searchFieldSx, chipSx, pillBtnSx, pillBtnPrimarySx, pillBtnDangerSx, dialogPaperSx, sectionHeaderSx, badgeSx, iconBtnSx, iconBtnDangerSx, eyebrowSx, displaySx } from './menuWorkspaceStyles'

const ModifierGroupsSubTab = () => {
  const { t } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingGroup, setEditingGroup] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)
  const [bulkError, setBulkError] = useState('')
  // True while the open editor has unsaved changes (reported by the builder).
  const [isDirty, setIsDirty] = useState(false)
  // Floating "saved" toast shown after the editor closes — visible regardless
  // of where the user had scrolled inside the form.
  const [toast, setToast] = useState('')

  const restaurantId = localStorage.getItem('restaurantId')
  const client = useApolloClient()

  const { data, loading: queryLoading } = useQuery(GET_ADVANCED_MODIFIER_GROUPS, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'network-only'
  })

  const [deleteGroup] = useMutation(DELETE_ADVANCED_MODIFIER_GROUP, {
    refetchQueries: [{ query: GET_ADVANCED_MODIFIER_GROUPS, variables: { restaurantId } }],
    onError: (err) => console.error('Delete error:', err)
  })

  const [duplicateGroup, { loading: duplicating }] = useMutation(DUPLICATE_ADVANCED_MODIFIER_GROUP, {
    // Await the refetch so the list (and cache) is fully refreshed before we open
    // the editor on the new group — avoids a race where the refetch re-normalizes
    // the cache after the editor populated, blanking the modifier list.
    refetchQueries: [{ query: GET_ADVANCED_MODIFIER_GROUPS, variables: { restaurantId } }],
    awaitRefetchQueries: true,
    onError: (err) => console.error('Duplicate error:', err)
  })

  const groups = data?.advancedModifierGroups || []

  const filteredGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    const result = q ? groups.filter(g => g.name.toLowerCase().includes(q)) : groups
    return result.slice().sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
  }, [groups, searchQuery])

  const handleOpenCreate = () => {
    setEditingGroup(null)
    setDialogOpen(true)
  }

  const handleOpenEdit = (group) => {
    setEditingGroup(group)
    setDialogOpen(true)
  }

  // Close the editor. After a successful save the builder clears its dirty flag,
  // so this only prompts when there are genuinely unsaved edits.
  const handleCloseDialog = ({ force = false } = {}) => {
    if (!force && isDirty) {
      if (!window.confirm(t('You have unsaved changes. Close without saving? Your changes will be lost.'))) return
    }
    setDialogOpen(false)
    setEditingGroup(null)
    setIsDirty(false)
    setRefreshKey(prev => prev + 1)
  }

  const handleDelete = (e, groupId) => {
    e.stopPropagation()
    if (window.confirm(t('Are you sure you want to delete this group?'))) {
      deleteGroup({ variables: { id: groupId } })
    }
  }

  // Duplicate an existing group: deep-copies the group + its modifiers into a new
  // independent group, then opens the editor on the copy so it can be renamed and
  // child groups / costs configured.
  const handleDuplicate = async (e, group) => {
    e.stopPropagation()
    const name = window.prompt(
      t('Name for the duplicated group:'),
      `${group.name} (Copy)`
    )
    if (name === null) return // cancelled
    const { data: dupData } = await duplicateGroup({
      variables: { id: group._id, name: name.trim() || undefined }
    })
    const created = dupData?.duplicateAdvancedModifierGroup
    if (!created) return
    // Prefer the freshly-refetched group from the list (same shape the edit path
    // uses, with fully-hydrated modifiers); fall back to the mutation response.
    const fromList = (client.readQuery({
      query: GET_ADVANCED_MODIFIER_GROUPS,
      variables: { restaurantId }
    })?.advancedModifierGroups || []).find(g => g._id === created._id)
    setEditingGroup(fromList || created)
    setDialogOpen(true)
  }

  const bulk = useBulkSelection(filteredGroups.map(g => g._id))

  const handleBulkDelete = async () => {
    const ids = bulk.selectedIds
    if (ids.length === 0) return
    if (!window.confirm(t('Delete {{n}} modifier groups?', { n: ids.length }))) return
    const { failed } = await bulkDelete(ids, (id) => deleteGroup({ variables: { id } }))
    bulk.clear()
    if (failed > 0) setBulkError(t('{{n}} groups could not be deleted.', { n: failed }))
  }

  const formatSelectionType = (group) => {
    if (group.selectionType === 'SINGLE') return t('Single')
    if (group.selectionType === 'EXACTLY') return `${t('Exactly')} ${group.exactSelections}`
    return `${group.minSelections} - ${group.maxSelections || '∞'}`
  }

  return (
    <Box>
      <Box sx={sectionHeaderSx}>
        <Typography variant="h4" fontWeight="bold" sx={{ color: mw.color.textPrimary }}>
          {t('Modifier Groups')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          {bulk.selectedIds.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleBulkDelete}
              sx={pillBtnDangerSx}
            >
              {t('Delete Selected')} ({bulk.selectedIds.length})
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={pillBtnPrimarySx}
          >
            {t('Create Group')}
          </Button>
        </Box>
      </Box>
      {bulkError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBulkError('')}>{bulkError}</Alert>}

      <Box sx={{ mb: 2.5, maxWidth: 380 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t('Search groups...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: mw.color.textFaint }} />
              </InputAdornment>
            )
          }}
          sx={searchFieldSx}
        />
      </Box>

      {/* Groups Table */}
      <TableContainer component={Paper} sx={tableCardSx}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: mw.color.headBg }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={bulk.allSelected}
                  indeterminate={bulk.someSelected}
                  onChange={bulk.toggleAll}
                  onClick={(e) => e.stopPropagation()}
                />
              </TableCell>
              <TableCell sx={headCellSx}>{t('Group Name')}</TableCell>
              <TableCell sx={headCellSx} align="center">{t('Modifiers')}</TableCell>
              <TableCell sx={headCellSx} align="center">{t('Selection')}</TableCell>
              <TableCell sx={headCellSx} align="center">{t('Required')}</TableCell>
              <TableCell sx={headCellSx} align="center">{t('Mode')}</TableCell>
              <TableCell sx={headCellSx} align="right" width={100}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {queryLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredGroups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6, color: mw.color.textMuted }}>
                  {groups.length === 0
                    ? t('No modifier groups yet. Click "Create Group" to get started.')
                    : t('No groups match your search.')}
                </TableCell>
              </TableRow>
            ) : (
              filteredGroups.map((group) => (
                <TableRow
                  key={group._id}
                  hover
                  sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }}
                  onClick={() => handleOpenEdit(group)}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={bulk.isSelected(group._id)}
                      onChange={() => bulk.toggle(group._id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" fontWeight={500} sx={{ color: mw.color.textPrimary, fontWeight: 700, fontSize: 15 }}>
                      {group.name}
                    </Typography>
                    {group.description && (
                      <Typography variant="caption" sx={{ color: mw.color.textMuted, fontSize: 13 }} noWrap display="block" maxWidth={250}>
                        {group.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {group.modifiers && group.modifiers.length > 0 ? (
                        <>
                          {group.modifiers.slice(0, 3).map((m) => (
                            <Chip
                              key={m._id}
                              label={m.modifier?.name || m.item?.name || '?'}
                              size="small"
                              sx={chipSx.category}
                            />
                          ))}
                          {group.modifiers.length > 3 && (
                            <Chip
                              label={`+${group.modifiers.length - 3}`}
                              size="small"
                              sx={chipSx.overflow}
                            />
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" sx={{ color: mw.color.textFaint }}>—</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ display: 'inline-flex', alignItems: 'center', height: 28, px: 1.25, border: `1px solid ${mw.color.border}`, borderRadius: mw.radius.badge, bgcolor: mw.color.cardBg, color: mw.color.textSecondary, fontWeight: 600, fontSize: 13, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {formatSelectionType(group)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {group.isRequired ? (
                      <Chip label={t('Required')} size="small" sx={badgeSx.required} />
                    ) : (
                      <Chip label={t('Optional')} size="small" sx={badgeSx.optional} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {group.isPizzaMode ? (
                      <Chip label={t('Pizza')} size="small" sx={badgeSx.pizza} />
                    ) : group.allowDuplicates ? (
                      <Chip label={t('Multi')} size="small" sx={badgeSx.multi} />
                    ) : (
                      <Typography variant="body2" sx={{ color: mw.color.textFaint }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: 'nowrap', pl: 1 }}>
                    <Tooltip title={t('Edit')}>
                      <IconButton size="small" onClick={() => handleOpenEdit(group)} sx={iconBtnSx}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('Duplicate group')}>
                      <span>
                        <IconButton size="small" disabled={duplicating} onClick={(e) => handleDuplicate(e, group)} sx={iconBtnSx}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('Delete')}>
                      <IconButton size="small" color="error" onClick={(e) => handleDelete(e, group._id)} sx={iconBtnDangerSx}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Group Dialog — reuse existing Column3GroupBuilder */}
      <Dialog
        open={dialogOpen}
        onClose={() => handleCloseDialog()}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { ...dialogPaperSx, maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ color: mw.color.textPrimary, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, px: 3.5, pt: 3, pb: 1.5, borderBottom: `1px solid ${mw.color.divider}` }}>
          {/* With a group name: small eyebrow + the name. Without: the title itself. */}
          <Box component="span" sx={{ display: 'block', minWidth: 0 }}>
            <Box component="span" sx={{ ...(editingGroup?.name ? eyebrowSx : displaySx(26)), display: 'block' }}>{editingGroup ? t('Edit Modifier Group') : t('Create Modifier Group')}</Box>
            {editingGroup?.name && (
              <Box component="span" sx={{ ...displaySx(26), display: 'block', mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {editingGroup.name}
              </Box>
            )}
          </Box>
          <Button onClick={() => handleCloseDialog()} size="small" sx={{ ...pillBtnSx, height: 40 }}>{t('Close')}</Button>
        </DialogTitle>
        <DialogContent sx={{ px: 3.5, pt: '20px !important' }}>
          <Column3GroupBuilder
            onGroupCreated={(msg) => {
              handleCloseDialog({ force: true })
              if (typeof msg === 'string' && msg) setToast(msg)
            }}
            refreshModifiers={refreshKey}
            initialGroup={editingGroup}
            onDirtyChange={setIsDirty}
          />
        </DialogContent>
      </Dialog>

      {/* Saved toast — floats over the page after the editor closes */}
      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setToast('')} sx={{ boxShadow: mw.shadow.pop, borderRadius: mw.radius.sm }}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default ModifierGroupsSubTab
